import { useContext, useEffect, useState } from "react";

import {
  Box,
  Typography,
  InputBase,
  IconButton,
  Drawer,
  Button,
  FormControl,
  MenuItem,
  Modal,
  TextField,
  Select,
  InputAdornment,
  Tooltip,
  Checkbox,
  FormHelperText,
} from "@mui/material";

import {
  CloudUpload,
  DeleteOutlined as Delete,
  OpenInNew,
  FileDownload,
  InsertDriveFile,
} from "@mui/icons-material";

import SearchIcon from "@mui/icons-material/Search";
import { Upload, message, Tree } from "antd";
// import type { UploadProps } from "antd";
import Container from "../base/Container";
import CloseIcon from "@mui/icons-material/Close";
import FolderIcon from "@mui/icons-material/Folder";
import {
  fetchFolderList,
  createFileshareFolder,
  deleteFileshareFolder,
  fetchFileshareFilesList,
  uploadFileshareFile,
  deleteFileshareFile,
  fetchFileshareFileUrl,
} from "../services/fileshare.service";
import { DataContext } from "../contexts/DataProvider";
import mp3 from "../assets/images/icons/audio.svg";
import vtt from "../assets/images/icons/file.svg";
import pdf from "../assets/images/icons/file.svg";
import image from "../assets/images/icons/image.svg";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"; // fallback
import ClearIcon from "@mui/icons-material/Clear";
import JSZip from "jszip";

const { DirectoryTree } = Tree;

interface FileItem {
  _id: string;
  fileName: string;
  fileUrl: string;
  fileType: string;
  folderName: string;
  folder?: string;
}

const FileListUI = () => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [folders, setFolders] = useState<{ title: string; key: string }[]>([]);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [selectedFolder, setSelectedFolder] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [uploadQueue, setUploadQueue] = useState<any[]>([]);
  const { login }: any = useContext(DataContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [folderError, setFolderError] = useState(false);
  const handleDeleteFolder = async (folderId: string) => {
    try {
      const response: any = await deleteFileshareFolder(folderId);

      if (response.success) {
        message.success("Folder and its files deleted successfully");
        // Refetch folders and files to update the UI
        fetchFolders();
        fetchFiles();
      } else {
        message.error(response.message || "Failed to delete folder.");
      }
    } catch (error) {
      console.error("Error deleting folder:", error);
      message.error("An error occurred while deleting the folder.");
    }
  };

  const handleDeleteMultipleFolders = async (folderIds: string[]) => {
    if (!folderIds || folderIds.length === 0) {
      message.error("No folders selected for deletion.");
      return;
    }

    console.log("Deleting folders with IDs:", folderIds);

    try {
      const deletionPromises = folderIds.map((folderId) =>
        deleteFileshareFolder(folderId),
      );

      const responses = await Promise.all(deletionPromises);

      const allSuccessful = responses.every(
        (response: any) => response.success,
      );

      if (!allSuccessful) {
        // If any of the responses were not successful, throw an error here.
        // The catch block in handleDeleteSelectedItems will now be triggered.
        const failedResponses = responses.filter(
          (response: any) => !response.success,
        );
        const errorMessages = failedResponses
          .map(
            (response: any) => response.message || "Failed to delete a folder.",
          )
          .join(", ");
        throw new Error(`Failed to delete some folders: ${errorMessages}`);
      } else {
        // If all deletions were successful, show a success message.
        message.success(
          "Selected folders and their files deleted successfully.",
        );
      }
    } catch (error) {
      console.error("Error deleting folders:", error);
      // Propagate the error so the outer function's catch block can handle it.
      throw error;
    } finally {
      // Always refetch to update the UI
      fetchFolders();
      fetchFiles();
    }
  };
  const handleDeleteSelectedItems = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      message.error("No items selected for deletion.");
      return;
    }

    try {
      // 1. Attempt to delete all selected IDs as folders.
      // We pass the array of IDs directly to your existing function.
      await handleDeleteMultipleFolders(selectedFiles);
      //  message.success("Selected folders deleted successfully.");
      // If the above line executes without an error, it means the
      // folders were successfully deleted by the backend.
      // Your existing handleDeleteMultipleFolders function will already
      // have refreshed the UI and shown a success message.
      return;
    } catch (error) {
      // 2. If the folder deletion fails for ANY reason (e.g., "Folder not found"),
      // we assume the selected IDs belong to files and try to delete them.
      console.log(
        "Folder deletion failed. Attempting to delete items as files.",
      );

      try {
        // Use Promise.all for efficient parallel deletion of files.
        const fileDeletionPromises = selectedFiles.map((fileId) =>
          deleteFileshareFile(fileId),
        );
        await Promise.all(fileDeletionPromises);
        message.success("Selected files deleted successfully.");
      } catch (fileError) {
        console.error("Error deleting files:", fileError);
        message.error("An error occurred while deleting the selected files.");
      }
    } finally {
      // This block is crucial for ensuring the UI is always up-to-date
      // after any deletion attempt.
      fetchFiles();
      fetchFolders();
    }
  };
  const handleCheckboxChange = (id: string) => {
    setSelectedFiles((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((fileId) => fileId !== id)
        : [...prevSelected, id],
    );
  };

  const renderIcon = (title: string, isFolder: boolean = false) => {
    if (isFolder) return <FolderIcon style={{ color: "#f4b400" }} />;

    if (
      title.toLowerCase().endsWith(".mp3") ||
      title.toLowerCase().endsWith(".wav") ||
      title.toLowerCase().endsWith(".aac") ||
      title.toLowerCase().endsWith(".flac")
    )
      return <img src={mp3} alt="mp3" style={{ width: 20, height: 20 }} />;

    if (title.toLowerCase().endsWith(".vtt"))
      return <img src={vtt} alt="vtt" style={{ width: 20, height: 20 }} />;
    if (title.toLowerCase().endsWith(".json"))
      return <img src={vtt} alt="vtt" style={{ width: 20, height: 20 }} />;
    if (
      title.toLowerCase().endsWith(".png") ||
      title.toLowerCase().endsWith(".jpg") ||
      title.toLowerCase().endsWith(".jpeg") ||
      title.toLowerCase().endsWith(".gif") ||
      title.toLowerCase().endsWith(".bmp") ||
      title.toLowerCase().endsWith(".svg")
    )
      return <img src={image} alt="vtt" style={{ width: 20, height: 20 }} />;

    if (title.toLowerCase().endsWith(".pdf"))
      return <img src={pdf} alt="pdf" style={{ width: 20, height: 20 }} />;
    return <InsertDriveFileIcon style={{ color: "#616161" }} />;
  };

  const handleFileAction = async (
    action: "view" | "download" | "delete",
    fileId: string,
    fileName: string,
  ) => {
    try {
      let response: any;
      switch (action) {
        case "view":
          response = await fetchFileshareFileUrl(fileId);
          if (response && response.success && response.fileUrl) {
            window.open(response.fileUrl, "_blank");
          } else {
            message.error("Failed to get view URL.");
          }
          break;
        case "download":
          response = await fetchFileshareFileUrl(fileId);
          if (response && response.success && response.fileUrl) {
            const link = document.createElement("a");
            link.href = response.fileUrl;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else {
            message.error("Failed to get download URL.");
          }
          break;
        case "delete":
          await handleDeleteFile(fileId);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(error);
      message.error("An error occurred during the file action.");
    }
  };

  const handleDownloadFolder = async (folderName: string) => {
    message.info(`Preparing to download folder "${folderName}"...`);
    try {
      const folderFiles = files.filter(
        (file: any) =>
          file.folder && file.folder.toLowerCase() === folderName.toLowerCase(),
      );

      if (folderFiles.length === 0) {
        message.warning("This folder is empty.");
        return;
      }

      const zip = new JSZip();
      const filesToDownload = [];

      for (const file of folderFiles) {
        const fileResponse: any = await fetchFileshareFileUrl(file._id);
        if (fileResponse?.success && fileResponse?.fileUrl) {
          filesToDownload.push({
            name: file.fileName,
            url: fileResponse.fileUrl,
          });
        } else {
          message.error(`Failed to get URL for file: ${file.fileName}`);
        }
      }

      await Promise.all(
        filesToDownload.map(async (file) => {
          try {
            const response = await fetch(file.url);
            const blob = await response.blob();
            zip.file(file.name, blob);
          } catch (e) {
            console.error(`Failed to fetch file: ${file.name}`, e);
            message.error(`Failed to download: ${file.name}`);
          }
        }),
      );

      const content = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(content);
      a.download = `${folderName}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      message.success(`Downloading "${folderName}.zip"`);
    } catch (error) {
      console.error("Download folder error:", error);
      message.error("An error occurred while downloading the folder.");
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedFiles.length === 0) {
      message.warning("Please select files or folders to download.");
      return;
    }

    message.info("Preparing to download selected items...");
    const zip = new JSZip();

    try {
      // Collect all unique file IDs to download
      const fileIdsToDownload = new Set();

      // Iterate through selected items
      for (const selectedId of selectedFiles) {
        // Check if the selected item is a folder
        const folder = folders.find((f) => f.key === selectedId);

        if (folder) {
          // If it's a folder, find all files inside it
          const folderFiles = files.filter(
            (file) =>
              file.folder &&
              file.folder.toLowerCase() === folder.title.toLowerCase(),
          );
          folderFiles.forEach((file) => fileIdsToDownload.add(file._id));
        } else {
          // If it's not a folder, assume it's a file
          fileIdsToDownload.add(selectedId);
        }
      }

      if (fileIdsToDownload.size === 0) {
        message.warning("No files found to download.");
        return;
      }

      // Now, iterate through the unique file IDs and add them to the zip
      for (const fileId of Array.from(fileIdsToDownload)) {
        const fileRecord = files.find((f) => f._id === fileId);

        if (fileRecord) {
          const fileResponse: any = await fetchFileshareFileUrl(fileRecord._id);
          if (fileResponse?.success && fileResponse?.fileUrl) {
            const response = await fetch(fileResponse.fileUrl);
            const blob = await response.blob();

            // Use the folder name to structure the zip file
            if (fileRecord.folder) {
              const folderZip = zip.folder(fileRecord.folder);
              folderZip.file(fileRecord.fileName, blob);
            } else {
              zip.file(fileRecord.fileName, blob);
            }
          } else {
            console.error(`Failed to get URL for file: ${fileRecord.fileName}`);
            message.error(`Failed to download: ${fileRecord.fileName}`);
          }
        }
      }

      const content = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(content);
      a.download = `selected_items.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      message.success("Downloading selected items...");
      setSelectedFiles([]); // Clear selected files after download
    } catch (error) {
      console.error("Bulk download error:", error);
      message.error("An error occurred during bulk download.");
    }
  };
  const buildTreeData = () => {
    const query = searchQuery.trim().toLowerCase();

    const folderNodes = folders
      .map((folder) => {
        const folderFiles = files.filter(
          (file) =>
            file.folder &&
            file.folder.toLowerCase() === folder.title.toLowerCase() &&
            (file.fileName.toLowerCase().includes(query) ||
              folder.title.toLowerCase().includes(query)),
        );

        if (
          folderFiles.length === 0 &&
          !folder.title.toLowerCase().includes(query)
        ) {
          return null;
        }

        return {
          title: (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
              onMouseEnter={() => setSelectedFileId(folder.key)}
              onMouseLeave={() => setSelectedFileId(null)}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                  overflow: "hidden",
                }}
              >
                <Checkbox
                  size="small"
                  sx={{ p: 0.5 }}
                  checked={selectedFiles.includes(folder.key)}
                  onChange={() => handleCheckboxChange(folder.key)}
                />
                {renderIcon(folder.title, true)}
                <Typography
                  sx={{
                    ml: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {folder.title}
                </Typography>
              </Box>
              {selectedFileId === folder.key && (
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadFolder(folder.title)}
                      sx={{ color: "#2e7d32" }}
                    >
                      <FileDownload fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteFolder(folder.key)}
                      sx={{ color: "#d32f2f" }}
                    >
                      <Delete fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          ),
          key: folder.key,
          icon: null,
          selectable: false,
          children: folderFiles.map((file) => ({
            title: (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
                onMouseEnter={() => setSelectedFileId(file._id)}
                onMouseLeave={() => setSelectedFileId(null)}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                    overflow: "hidden",
                  }}
                >
                  <Checkbox
                    size="small"
                    sx={{ p: 0.5 }}
                    checked={selectedFiles.includes(file._id)}
                    onChange={() => handleCheckboxChange(file._id)}
                  />
                  {renderIcon(file.fileName)}
                  <Typography
                    sx={{
                      ml: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {file.fileName}
                  </Typography>
                </Box>
                {selectedFileId === file._id && (
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleFileAction("view", file._id, file.fileName)
                        }
                        sx={{ color: "#1976d2" }}
                      >
                        <OpenInNew fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleFileAction("download", file._id, file.fileName)
                        }
                        sx={{ color: "#2e7d32" }}
                      >
                        <FileDownload fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleFileAction("delete", file._id, file.fileName)
                        }
                        sx={{ color: "#d32f2f" }}
                      >
                        <Delete fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            ),
            key: file._id,
            icon: renderIcon(file.fileName),
            isLeaf: true,
          })),
        };
      })
      .filter(Boolean);

    return folderNodes;
  };
  const handleFileSelect = (info: any) => {
    setUploadQueue((prev) => {
      // Create a Set of unique UIDs from the existing queue for efficient lookup.
      const existingUids = new Set(prev.map((file) => file.uid));

      // Filter the new file list to include only files that are not already in the queue.
      const newFiles = info.fileList.filter(
        (file: any) => !existingUids.has(file.uid),
      );

      // Map the new files to the desired object format.
      const filesToAdd = newFiles.map((file: any) => ({
        uid: file.uid,
        name: file.name,
        status: "pending",
        percent: 0,
        raw: file.originFileObj,
      }));

      // Return the combined array of previous files and the newly filtered files.
      return [...prev, ...filesToAdd];
    });
  };

  const handleUploadSave = async () => {
    if (!selectedFolder) {
      setFolderError(true); // Set the error state to true
      // message.error("Please select a folder.");
      return; // Stop the function from proceeding
    }

    // 2. Clear the error state if a folder is selected
    setFolderError(false);
    for (const file of uploadQueue) {
      try {
        let progress = 0;
        const interval = setInterval(() => {
          progress += 5;
          if (progress < 90) {
            setUploadQueue((prev) =>
              prev.map((f) =>
                f.uid === file.uid
                  ? { ...f, percent: progress, status: "uploading" }
                  : f,
              ),
            );
          }
        }, 300);

        const formData = new FormData();
        formData.append("files", file.raw);
        formData.append("orgId", login.data.orgId);
        formData.append("userId", login.data.id);
        formData.append("fileType", file.raw.type || "unknown");
        formData.append("fileName", file.name);
        formData.append("folderName", selectedFolder || "General");

        await uploadFileshareFile(formData);

        clearInterval(interval);

        setUploadQueue((prev) =>
          prev.map((f) =>
            f.uid === file.uid ? { ...f, percent: 100, status: "done" } : f,
          ),
        );
      } catch (err) {
        console.error(err);
        setUploadQueue((prev) =>
          prev.map((f) => (f.uid === file.uid ? { ...f, status: "error" } : f)),
        );
      }
    }

    fetchFiles();
    message.success("Upload finished");
    setDrawerVisible(false);
    setUploadQueue([]);
  };

  const handleRemoveFromQueue = (uid: string) => {
    setUploadQueue((prev) => prev.filter((f) => f.uid !== uid));
  };

  const handleAddFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const response: any = await createFileshareFolder({
        name: newFolderName.trim(),
        orgId: login.data.orgId,
        userId: login.data.id,
      });

      if (response.success) {
        setFolders([
          ...folders,
          { title: response.folder.name, key: response.folder._id },
        ]);
        message.success(`Folder "${response.folder.name}" created`);
        setNewFolderName("");
        setOpenModal(false);
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to create folder.");
    }
  };

  const fetchFolders = async () => {
    try {
      const response: any = await fetchFolderList(login.data.orgId);
      if (response && response.folders) {
        setFolders(
          response.folders.map((f: any) => ({ title: f.name, key: f._id })),
        );
      }
    } catch (error: any) {
      // Access error.data for your custom fields
      console.log("Error fetching folders:", error);
    }
  };

  const fetchFiles = async () => {
    try {
      const response: any = await fetchFileshareFilesList();
      if (response && response.files) {
        setFiles(response.files);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (login && login.data) {
      fetchFolders().then(() => {
        fetchFiles();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login]);

  // const props: UploadProps = {
  //   name: "files",
  //   multiple: true,
  //   customRequest: async ({ file, onSuccess, onError }) => {
  //     try {
  //       const formData = new FormData();
  //       // file can be type RcFile from antd, so access safely
  //       const fileObj = file as any;
  //       formData.append("files", file as Blob);
  //       formData.append("orgId", login.data.orgId);
  //       formData.append("userId", login.data.id);
  //       formData.append("fileType", fileObj?.type || "unknown");
  //       formData.append("fileName", fileObj?.name || "unknown");
  //       formData.append("folderName", selectedFolder || "General");

  //       await httpClient("/fileshare", {
  //         method: "POST",
  //         body: formData,
  //       });

  //       message.success(`${fileObj?.name || "File"} uploaded successfully`);
  //       if (onSuccess) onSuccess({}, fileObj);
  //       fetchFiles();
  //     } catch (error) {
  //       console.error(error);
  //       message.error(`${(file as any)?.name || "File"} upload failed.`);
  //       if (onError) onError(error as any);
  //     }
  //   },
  // };

  const handleDeleteFile = async (fileId: string) => {
    try {
      await deleteFileshareFile(fileId);
      message.success("File deleted successfully");
      fetchFiles();
    } catch (error) {
      console.error(error);
      message.error("Failed to delete file.");
    }
  };

  return (
    <Box sx={{ width: "100%", bgcolor: "#fafafa" }}>
      <Container>
        <Box sx={{ width: "100%", bgcolor: "#fafafa" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            gap={2}
            borderRadius={"8px"}
            bgcolor={"#ffff"}
            border={"1px solid #DFDFDF"}
            padding="6px 12px"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Typography
                style={{
                  whiteSpace: "nowrap",
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 700,
                }}
              >
                Files
              </Typography>
              <InputBase
                placeholder="Search by file name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "#757575" }} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    {searchQuery && (
                      <IconButton
                        aria-label="clear search"
                        onClick={() => setSearchQuery("")}
                        edge="end"
                        size="small"
                      >
                        <ClearIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                }
                sx={{
                  backgroundColor: "#ffff",
                  px: 1,
                  ml: 1,
                  borderRadius: 2,
                  height: "38px",
                  border: "1px solid #DFDFDF",
                  width: "272px",
                  "& .MuiInputBase-input": {
                    pl: 1,
                  },
                }}
              />
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              {selectedFiles.length > 0 && (
                <>
                  {/* Download button using an external SVG file */}
                  <Button
                    // variant="inline"
                    // color="primary"
                    onClick={handleDownloadSelected}
                    // sx={{ minWidth: 40, width: 40, p: 1, borderRadius: '50%' }}
                  >
                    <img
                      src="/bulkdownload.svg"
                      alt="Download"
                      style={{ width: 50, height: 50, marginRight: "-20px" }}
                    />
                  </Button>

                  {/* New Delete button using an external SVG file */}
                  <Button
                    // variant="contained"
                    // color="error"
                    onClick={handleDeleteSelectedItems} // sx={{ minWidth: 40, width: 40, p: 1, borderRadius: '50%' }}
                  >
                    <img
                      src="/bulkdelete.svg"
                      alt="Delete"
                      style={{ width: 50, height: 50 }}
                    />
                  </Button>
                </>
              )}

              {/* The existing Upload File button */}
              <Button
                variant="contained"
                color="primary"
                onClick={() => setDrawerVisible(true)}
                startIcon={<CloudUpload />}
              >
                Upload File
              </Button>
            </Box>
          </Box>

          <Box
            border="1px solid #DFDFDF"
            borderRadius={2}
            bgcolor="white"
            sx={{
              "& .ant-tree-treenode": {
                padding: "6px 8px",
                borderRadius: "4px",
                marginBottom: "4px",
              },
              "& .ant-tree-treenode:hover": {
                backgroundColor: "#f9f9f9",
              },
              "& .ant-tree-switcher": {
                marginRight: "6px",
              },
              "& .ant-tree-title": {
                fontSize: "14px",
                fontWeight: 500,
              },
              "& .ant-tree-treenode-selected:hover": {
                backgroundColor: "transparent",
              },
              "& .ant-tree-node-content-wrapper-normal:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <DirectoryTree
              treeData={buildTreeData()}
              showIcon={false}
              defaultExpandAll={false}
              defaultExpandedKeys={[]}
              expandAction="click"
              selectable={false}
            />
          </Box>

          <Drawer
            anchor="right"
            open={drawerVisible}
            onClose={() => {
              setDrawerVisible(false);
              setUploadQueue([]);
              setSelectedFolder("");
              setFolderError(false);
            }}
            sx={{
              "& .MuiDrawer-paper": {
                width: 460,
                boxSizing: "border-box",
              },
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6">Upload File</Typography>
                  <IconButton
                    aria-label="close"
                    onClick={() => setDrawerVisible(false)}
                    sx={{ p: 0 }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>

                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: "block", mb: 2, mt: 1 }}
                >
                  Upload your file here.
                </Typography>

                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <FormControl size="small" fullWidth error={folderError}>
                    <Select
                      value={selectedFolder}
                      onChange={(e) => {
                        setSelectedFolder(e.target.value);
                        if (e.target.value) {
                          setFolderError(false);
                        }
                      }}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Choose a folder</em>
                      </MenuItem>
                      {folders.map((folder) => (
                        <MenuItem key={folder.key} value={folder.title}>
                          {folder.title}
                        </MenuItem>
                      ))}
                    </Select>
                    {folderError && (
                      <FormHelperText>Please select a Folder</FormHelperText>
                    )}
                  </FormControl>
                  <IconButton
                    color="primary"
                    onClick={() => setOpenModal(true)}
                  >
                    <FolderIcon />
                  </IconButton>
                </Box>

                <Box mb={2} sx={{ height: "30vh" }}>
                  <Upload.Dragger
                    multiple
                    showUploadList={false}
                    beforeUpload={() => false}
                    onChange={handleFileSelect}
                    style={{ padding: 16, borderRadius: 8, height: "20vh" }}
                  >
                    <p className="ant-upload-drag-icon">
                      <CloudUpload style={{ color: "#1890ff" }} />
                    </p>
                    <p className="ant-upload-text">
                      Drop your files here or click to upload
                    </p>
                  </Upload.Dragger>
                </Box>

                <Box
                  mt={3}
                  sx={{
                    border: "1px solid #DFDFDF",
                    borderRadius: "8px",
                    p: 2,
                    bgcolor: "white",
                  }}
                >
                  <Typography variant="subtitle1" mb={1}>
                    Files History
                  </Typography>
                  {uploadQueue.map((file) => (
                    <Box
                      key={file.uid}
                      display="flex"
                      flexDirection="column"
                      p={1.5}
                      border="1px solid #ddd"
                      borderRadius={2}
                      mb={1.5}
                      bgcolor="#fafafa"
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          flex={1}
                          sx={{ minWidth: 0 }} // The key CSS fix
                        >
                          <InsertDriveFile fontSize="small" />
                          <Tooltip title={file.name}>
                            <Typography variant="body2" noWrap flex={1}>
                              {file.name}
                            </Typography>
                          </Tooltip>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFromQueue(file.uid)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>

                      {file.status === "uploading" && (
                        <Box mt={1}>
                          <Box
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: "#e0e0e0",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                width: `${file.percent}%`,
                                height: "100%",
                                backgroundColor: "#1890ff",
                                transition: "width 0.3s ease",
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="textSecondary">
                            {file.percent}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box
                sx={{
                  mt: "auto",
                  p: 2,
                  borderTop: "1px solid #eee",
                  bgcolor: "white",
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <Button onClick={() => setDrawerVisible(false)}>Cancel</Button>
                <Button variant="contained" onClick={handleUploadSave}>
                  Save
                </Button>
              </Box>
            </Box>
          </Drawer>
        </Box>

        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 3,
              borderRadius: 2,
              width: 400,
            }}
          >
            <Typography variant="h6" mb={2}>
              Create New Folder
            </Typography>
            <TextField
              fullWidth
              label="Folder Name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button onClick={() => setOpenModal(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleAddFolder}>
                Create
              </Button>
            </Box>
          </Box>
        </Modal>
      </Container>
    </Box>
  );
};

export default FileListUI;
